import type { RoadSection, RoadSectionId } from '../domain/Reference';
import { formatSectionLabel } from './format';

// Search box of the map: finds an axis, or a section by its axis, label or PR ("d888", "D888 PR 12").

export interface SearchEntry {
    section: RoadSection;
    label: string;
    // Normalized text the query is looked up in.
    text: string;
}

export type SearchResult =
    | { kind: 'axis'; roadName: string; count: number; firstSectionId: RoadSectionId }
    | { kind: 'section'; sectionId: RoadSectionId; label: string; roadName: string };

export const MIN_QUERY_LENGTH = 2;
const MAX_AXES = 3;
const MAX_SECTIONS = 5;

// Lower case, without accents, with single spaces: "Départementale  D888" -> "departementale d888".
export const normalize = (text: string): string =>
    text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/\s+/g, ' ').trim();

// Built once per loaded référentiel, so typing does not re-format thousands of labels.
export const buildSearchIndex = (sections: RoadSection[]): SearchEntry[] =>
    sections.map((section) => {
        const label = formatSectionLabel(section);

        return { section, label, text: normalize(`${label} ${section.roadName} ${section.name ?? ''}`) };
    });

export const searchSections = (index: SearchEntry[], query: string): SearchResult[] => {
    const wanted = normalize(query);
    if (wanted.length < MIN_QUERY_LENGTH) {
        return [];
    }

    const axes = new Map<string, { count: number; first: SearchEntry }>();
    index.forEach((entry) => {
        const name = normalize(entry.section.roadName);
        if (name.includes(wanted)) {
            const known = axes.get(entry.section.roadName);
            axes.set(entry.section.roadName, { count: (known?.count ?? 0) + 1, first: known?.first ?? entry });
        }
    });
    const axisResults: SearchResult[] = Array.from(axes.entries())
        // Axes that start with the query first, then the shortest names (closest to what was typed).
        .sort(([a], [b]) => {
            const startsA = normalize(a).startsWith(wanted) ? 0 : 1;
            const startsB = normalize(b).startsWith(wanted) ? 0 : 1;

            return startsA - startsB || a.length - b.length || a.localeCompare(b, 'fr');
        })
        .slice(0, MAX_AXES)
        .map(([roadName, { count, first }]) => ({
            kind: 'axis',
            roadName,
            count,
            firstSectionId: first.section.id,
        }));

    // Every word typed must appear in the section's text, in any order: "d888 pr 13" finds "D888 - PR 12+450 à 13+250".
    const words = wanted.split(' ');
    const sectionResults: SearchResult[] = [];
    for (const entry of index) {
        if (sectionResults.length >= MAX_SECTIONS) {
            break;
        }
        if (words.every((word) => entry.text.includes(word))) {
            sectionResults.push({
                kind: 'section',
                sectionId: entry.section.id,
                label: entry.label,
                roadName: entry.section.roadName,
            });
        }
    }

    return [...axisResults, ...sectionResults];
};
